import { Box, useToast } from '@chakra-ui/react';
import assert from 'assert';
import React from 'react';
import { Link as RouteLink } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import {
  UpdateFundingRequestComponentChangesetGQLType,
  FundingRequestComponentTypeEnumGQLType,
  FundingRequestStatusEnumGQLType,
  FundingRequestWithComponentsAndBankAccountsFragmentGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';
import { BankAccountMap } from 'types/fundingRequests';

import {
  FundingRequestComponentsTable,
  isFundingRequestComponentsTableParams,
} from 'borrower/domains/fundingRequests/ui/shared/ComponentsTable';
import { LoadingView } from 'borrower/domains/fundingRequests/ui/shared/Loading';
import { WhiteSubmitButton } from 'packages/common-frontend/components/FormField';
import { PageHeader } from 'packages/common-frontend/components/PageContainer';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { useGetFundingRequestWithFullDetailsAndConstraintQuery } from 'packages/funding-requests/requests/requests.gql.generated';
import { FiArrowRight } from 'react-icons/fi';

/**
 * Helper function to create view link
 */
const getViewLinkParams = (id: string, status: string): [string, string] => {
  const baseUrl = `/funding-requests/${id}`;
  if (status === 'pending') {
    return ['Review', baseUrl];
  }
  return ['View', `${baseUrl}#monitor`];
};

const landingPageStatusLabels: {
  [key in FundingRequestStatusEnumGQLType]?: {
    headerLabel: string;
    subheaderLabel: string;
  };
} = {
  pending: {
    headerLabel: 'Review and submit draft funding request',
    subheaderLabel: 'Funding Request is awaiting your review and submission.',
  },
  submitted: {
    headerLabel: 'View submitted funding request',
    subheaderLabel: 'Funding Request is awaiting capital provider approval.',
  },
  approved: {
    headerLabel: 'View approved funding request',
    subheaderLabel: 'Your request has been approved.',
  },
};

/**
 * Header selection logic
 */
const getHeaderDesc = (status: FundingRequestStatusEnumGQLType) => {
  const statusLabel = landingPageStatusLabels[status];
  if (!statusLabel) {
    return <></>;
  }
  return (
    <PageHeader
      headerLabel={statusLabel.headerLabel}
      subheaderLabel={statusLabel.subheaderLabel}
    />
  );
};

/**
 * Footer selection logic
 */
const getFooterDesc = (status: string) => {
  if (status !== 'submitted') {
    return null;
  }

  return (
    <Box pt={20}>
      <PageHeader
        headerLabel="Initiate new funding request"
        subheaderLabel="To initiate a new funding request, please view and cancel any existing funding requests."
      />
    </Box>
  );
};

/**
 * Actual React Component to show current Funding Request landing page
 */
export const FundingRequestLandingCurrentRequest = ({
  currentFundingRequest,
  bankAccountMap,
}: {
  currentFundingRequest: FundingRequestWithComponentsAndBankAccountsFragmentGQLType;
  bankAccountMap: BankAccountMap;
}) => {
  const toast = useToast();
  const { id, status, fundingRequestComponents } = currentFundingRequest;
  // NOTE: All down-stream helper functions relies on the fact we're only passing in supported status for this page, so we will assert
  // for valid status here.

  const {
    loading,
    data: getFundingRequestWithFullDetailsAndConstraintData,
  } = useGetFundingRequestWithFullDetailsAndConstraintQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: {
      fundingRequestId: id,
    },
    fetchPolicy: 'network-only',
  });
  if (loading || !getFundingRequestWithFullDetailsAndConstraintData) {
    return <LoadingView />;
  }
  assert(
    status === 'pending' || status === 'submitted' || status === 'approved',
    'Unsupported FundingRequest status',
  );

  const {
    fundingRequestConstraint: {
      isCollectionsRecycleEnabled,
      isVoluntaryPaydownAllowed,
      maxFundingRequestAmount,
    },
  } = getFundingRequestWithFullDetailsAndConstraintData;

  const [linkText, linkUrl] = getViewLinkParams(id, status);
  const headerDesc = getHeaderDesc(status);
  const footerDesc = getFooterDesc(status);

  // transform list of components to map
  // TODO: use map here
  const components = fundingRequestComponents.reduce((acc, component) => {
    const _acc = { ...acc };
    if (!_acc[component.type]) {
      _acc[component.type] = [];
    }
    _acc[component.type].push(component);

    return _acc;
  }, {} as { [key in FundingRequestComponentTypeEnumGQLType]: UpdateFundingRequestComponentChangesetGQLType[] });

  assert(
    isFundingRequestComponentsTableParams(components),
    'Funding request does not contain all necesary components to render componentsTable',
  );
  return (
    <Box>
      {headerDesc}
      <FundingRequestComponentsTable
        fundingRequestComponents={components}
        isCollectionsRecycleEnabled={isCollectionsRecycleEnabled}
        isVoluntaryPaydownAllowed={isVoluntaryPaydownAllowed}
        maxFundingRequestAmount={maxFundingRequestAmount}
        bankAccountMap={bankAccountMap}
      />
      <RouteLink to={linkUrl}>
        <WhiteSubmitButton label={linkText} rightIcon={<FiArrowRight />} />
      </RouteLink>
      {footerDesc}
    </Box>
  );
};
